/**
 * How the receipt-reading AI performs — TRBUD-467, the "AI receipt accuracy"
 * page at `/reports/receipt-accuracy`.
 *
 * ## What one row is
 *
 * One RECEIPT is one visible item in the Review queue: pages joined into one
 * receipt (TRBUD-352) count once, and a split bill (TRBUD-318) is its own
 * receipt. MaintainX imports and orphan items are left out, and only invoices
 * and receipts count. A receipt belongs to the fleet week (Monday start) it
 * ARRIVED in.
 *
 * ## Counts travel, shares do not
 *
 * The server sends counts only; the screen works out each share with
 * `shareOf`, which answers null when there is nothing to divide. So a page with
 * no judged receipts says so, and never prints 0%.
 *
 * ## Worked out when read
 *
 * Nothing here is stored. The figures come from the extractions, the saved
 * invoices and `parser_feedback` as they stand, so the page cannot disagree
 * with the Review queue. It counts RECEIPTS, so it will not match the per-version
 * read counts on Settings → Receipt reading, which count reads.
 *
 * Browser-safe: zod, types, labels and arithmetic.
 */
import { z } from "zod";
import type { ReceiptReadingMode, ReceiptReadingModeBasis } from "./receipt-parser.js";

/* ── The window ──────────────────────────────────────────────────── */

/** How many weeks the page may look back. No "all time" (Eden, 30 October 2026). */
export const receiptAccuracyWeekChoices = [4, 13, 26, 52] as const;
export type ReceiptAccuracyWeeks = (typeof receiptAccuracyWeekChoices)[number];
export const RECEIPT_ACCURACY_DEFAULT_WEEKS: ReceiptAccuracyWeeks = 13;

/**
 * `GET /reports/receipt-accuracy`. Strict, so a misspelt filter is a 400 rather
 * than a page that silently ignored it (TRBUD-152).
 */
export const receiptAccuracyQuery = z
  .object({
    weeks: z
      .enum(["4", "13", "26", "52"])
      .default(String(RECEIPT_ACCURACY_DEFAULT_WEEKS) as "13")
      .transform((value) => Number(value) as ReceiptAccuracyWeeks),
    /** One AI version: only its reads count, and only receipts it read. */
    parserId: z.uuid().optional(),
    /** One vendor, by the receipt's vendor (see `ReceiptAccuracyReport`). */
    vendorId: z.uuid().optional(),
  })
  .strict();
export type ReceiptAccuracyQuery = z.output<typeof receiptAccuracyQuery>;

/* ── Shares ──────────────────────────────────────────────────────── */

/**
 * `n` out of `of`, or null when there is nothing to divide. The one division on
 * the page, so no figure can read 0% for "nothing judged yet".
 */
export function shareOf(n: number, of: number): number | null {
  if (!Number.isFinite(n) || !Number.isFinite(of) || of <= 0) return null;
  return n / of;
}

/** Under this many judged, a share carries "few receipts so far". */
export const RECEIPT_ACCURACY_FEW_BELOW = 20;

/**
 * `84% (42 of 50)`, rounded DOWN so 99.6% never reads as 100%, or null when
 * there is nothing to divide.
 */
export function shareText(n: number, of: number): string | null {
  const share = shareOf(n, of);
  if (share === null) return null;
  return `${Math.floor(share * 100)}% (${n.toLocaleString()} of ${of.toLocaleString()})`;
}

/* ── What happened to each receipt ───────────────────────────────── */

/**
 * Every receipt is in exactly ONE of these. Where the pages of one receipt
 * disagree, `receiptStateOf` in the API decides, in this order: nothing to log,
 * then still reading, stuck, read, failed, file not allowed, not sent. That is
 * the Review screen's own rule for a receipt of several pages (still reading
 * while any page is, read once any page is).
 */
export const receiptVolumeStates = [
  "not_sent",
  "reading",
  "stuck",
  "failed",
  "file_not_allowed",
  "read",
  "nothing_to_log",
] as const;
export type ReceiptVolumeState = (typeof receiptVolumeStates)[number];

export const receiptVolumeStateLabels: Record<ReceiptVolumeState, string> = {
  not_sent: "Not sent to the AI",
  reading: "Still reading",
  stuck: "Stuck",
  failed: "Failed",
  file_not_allowed: "File not allowed",
  read: "Read",
  nothing_to_log: "Nothing to log",
};

/**
 * Why a read failed. From the fixed sentences `receipt-parser/run.ts` stores,
 * and its `timedOut` flag. `other` is a sentence none of them starts.
 */
export const receiptReadFailureKinds = ["refused", "call_failed", "timed_out", "not_stored", "other"] as const;
export type ReceiptReadFailureKind = (typeof receiptReadFailureKinds)[number];

export const receiptReadFailureLabels: Record<ReceiptReadFailureKind, string> = {
  refused: "AI answer refused",
  call_failed: "Call failed",
  timed_out: "Timed out",
  not_stored: "Could not be stored",
  other: "Other",
};

/* ── The fields ──────────────────────────────────────────────────── */

export const receiptFieldGroups = ["document", "unit", "line"] as const;
export type ReceiptFieldGroup = (typeof receiptFieldGroups)[number];

/**
 * The fields the found rate counts. A field is FOUND when the AI's read holds a
 * value that is not empty (or a list that is not empty). Document and unit
 * fields are counted out of judged reads; line fields out of the lines read.
 */
export const RECEIPT_FIELDS = [
  { key: "vendorName", label: "Vendor", group: "document" },
  { key: "vendorAddress", label: "Vendor address", group: "document" },
  { key: "vendorPhone", label: "Vendor phone", group: "document" },
  { key: "invoiceNumber", label: "Invoice number", group: "document" },
  { key: "invoiceDate", label: "Invoice date", group: "document" },
  { key: "serviceDate", label: "Service date", group: "document" },
  { key: "otherReferences", label: "Other references", group: "document" },
  { key: "currency", label: "Currency", group: "document" },
  { key: "subtotal", label: "Subtotal", group: "document" },
  { key: "taxTotal", label: "Tax", group: "document" },
  { key: "total", label: "Total", group: "document" },
  { key: "unitNumber", label: "Unit number", group: "unit" },
  { key: "vin", label: "VIN", group: "unit" },
  { key: "year", label: "Year", group: "unit" },
  { key: "make", label: "Make", group: "unit" },
  { key: "model", label: "Model", group: "unit" },
  { key: "odometer", label: "Odometer", group: "unit" },
  { key: "engineHours", label: "Engine hours", group: "unit" },
  { key: "description", label: "Line description", group: "line" },
  { key: "partNumber", label: "Part number", group: "line" },
  { key: "quantity", label: "Quantity", group: "line" },
  { key: "unitOfMeasure", label: "Unit of measure", group: "line" },
  { key: "unitPrice", label: "Unit price", group: "line" },
  { key: "amount", label: "Line amount", group: "line" },
] as const satisfies ReadonlyArray<{ key: string; label: string; group: ReceiptFieldGroup }>;
export type ReceiptFieldKey = (typeof RECEIPT_FIELDS)[number]["key"];

/* ── Accuracy ────────────────────────────────────────────────────── */

/** The header fields compared with what was saved. */
export const receiptHeaderFields = ["vendor", "invoiceNumber", "invoiceDate", "total", "unit", "vin"] as const;
export type ReceiptHeaderField = (typeof receiptHeaderFields)[number];

export const receiptHeaderFieldLabels: Record<ReceiptHeaderField, string> = {
  vendor: "Vendor",
  invoiceNumber: "Invoice number",
  invoiceDate: "Invoice date",
  total: "Total",
  unit: "Unit",
  vin: "VIN",
};

/**
 * One header field of one receipt, judged:
 *
 * - `right` / `wrong`: both have a value, and they agree or do not.
 * - `missed`: the saved bill has a value and the AI found none.
 * - `not_on_receipt`: neither has one. Never an error.
 * - `not_comparable`: nothing to compare against: a restock bill on a purchase
 *   order has no job (so no unit or VIN), the job is one the viewer cannot see
 *   (someone else's draft), or TruckBuddy does not hold the value.
 * - `unrecognised` (vendor only): the AI read a name that matches no
 *   TruckBuddy vendor. Kept out of the share: the AI may have read it right.
 *
 * Share = right / (right + wrong).
 */
export const receiptHeaderOutcomes = ["right", "wrong", "missed", "not_on_receipt", "not_comparable", "unrecognised"] as const;
export type ReceiptHeaderOutcome = (typeof receiptHeaderOutcomes)[number];

export const receiptHeaderOutcomeLabels: Record<ReceiptHeaderOutcome, string> = {
  right: "Right",
  wrong: "Wrong",
  missed: "Missed",
  not_on_receipt: "Not on receipt",
  not_comparable: "Not comparable",
  unrecognised: "Read but not recognised",
};

/** The line fields compared, by the comparisons the save itself uses. */
export const receiptLineFields = ["description", "kind", "quantity", "unitPrice", "unit", "amount"] as const;
export type ReceiptLineField = (typeof receiptLineFields)[number];

export const receiptLineFieldLabels: Record<ReceiptLineField, string> = {
  description: "Description",
  kind: "Kind",
  quantity: "Quantity",
  unitPrice: "Unit price",
  unit: "Labour unit",
  amount: "Amount",
};

/* ── Confidence ──────────────────────────────────────────────────── */

/** Confidence bands, lowest first. Each band includes its lower edge. */
export const receiptConfidenceBands = ["under_50", "50_70", "70_80", "80_90", "90_95", "95_up"] as const;
export type ReceiptConfidenceBand = (typeof receiptConfidenceBands)[number];

export const receiptConfidenceBandLabels: Record<ReceiptConfidenceBand, string> = {
  under_50: "Under 50%",
  "50_70": "50–70%",
  "70_80": "70–80%",
  "80_90": "80–90%",
  "90_95": "90–95%",
  "95_up": "95% and up",
};

/** The band a 0–1 score falls in: 0.5 is in 50–70%, 0.95 in 95% and up. */
export function confidenceBandOf(confidence: number): ReceiptConfidenceBand {
  if (confidence < 0.5) return "under_50";
  if (confidence < 0.7) return "50_70";
  if (confidence < 0.8) return "70_80";
  if (confidence < 0.9) return "80_90";
  if (confidence < 0.95) return "90_95";
  return "95_up";
}

/** What a score was given for: a line, the vendor, or the unit. Each has its own score. */
export const receiptConfidenceKinds = ["line", "vendor", "unit"] as const;
export type ReceiptConfidenceKind = (typeof receiptConfidenceKinds)[number];

/* ── The reply ───────────────────────────────────────────────────── */

export interface ReceiptLoggedSplit {
  /** Saved from the AI's read: the save named it. */
  savedFromRead: number;
  /** Typed by hand while an AI read was ready (it finished before the bill was saved). */
  typedWithReadAvailable: number;
  /** Logged with no AI read ready. */
  typedWithoutRead: number;
}

export interface ReceiptVolume {
  receipts: number;
  states: Record<ReceiptVolumeState, number>;
  /** The failed receipts, by why. Adds up to `states.failed`. */
  failures: Record<ReceiptReadFailureKind, number>;
  logged: ReceiptLoggedSplit;
  /** AI calls: reads the AI made itself, so a split bill's copy of a read is not one. */
  aiCalls: number;
}

/** One vendor's found counts. `key` is a vendor id, `unknown` or `others`. */
export interface ReceiptFoundRow {
  key: string;
  label: string;
  /** Receipts with a judged AI read: what document and unit fields are out of. */
  judged: number;
  /** Lines in those reads: what line fields are out of. */
  lines: number;
  found: Record<ReceiptFieldKey, number>;
}

export interface ReceiptLineAccuracy {
  /** Receipts saved from the AI's read: the only ones line accuracy counts. */
  receipts: number;
  /** AI lines the person kept as read. */
  kept: number;
  /** AI lines the person changed. */
  changed: number;
  /** AI lines the person removed. */
  removed: number;
  /** Lines the person added that the AI did not read. */
  missed: number;
  /** Per field, over kept and changed lines. */
  fields: Record<ReceiptLineField, { right: number; wrong: number }>;
}

export interface ReceiptAccuracy {
  /** Receipts whose header was judged: saved from a read, plus typed while one was ready. */
  headerReceipts: number;
  /** Of those, typed without using the read: counted for header fields only. */
  typedHeaderOnly: number;
  header: Record<ReceiptHeaderField, Record<ReceiptHeaderOutcome, number>>;
  lines: ReceiptLineAccuracy;
}

/** One row of the weekly, version or vendor breakdown. */
export interface ReceiptTrendRow {
  /** A week's Monday, a parser id, or a vendor key. */
  key: string;
  label: string;
  /** Receipts; on a version row, page reads (a receipt read twice counts twice). */
  receipts: number;
  read: number;
  failed: number;
  /** Logged so far. */
  logged: number;
  linesJudged: number;
  linesKept: number;
  headerJudged: number;
  headerRight: number;
}

export interface ReceiptVersionRow extends ReceiptTrendRow {
  parserName: string;
  parserVersion: string;
  /** The version's CURRENT flag line, not the one it had when it read. */
  threshold: number;
}

export interface ReceiptConfidenceRow {
  kind: ReceiptConfidenceKind;
  band: ReceiptConfidenceBand;
  /** Under the version's current threshold. */
  flagged: boolean;
  /** From a file the AI marked handwritten. */
  handwritten: boolean;
  judged: number;
  right: number;
}

export type ReceiptCorrectionKind = "line_changed" | "line_removed" | "header_wrong";

export interface ReceiptCorrection {
  at: string;
  /** The Review item: `/review?item=` opens it. */
  itemId: string;
  kind: ReceiptCorrectionKind;
  /** A label, e.g. "Line 3 · Amount" or "Invoice number". */
  field: string;
  aiValue: string | null;
  savedValue: string | null;
  vendorLabel: string | null;
  parserVersion: string;
}

export interface ReceiptAccuracyOption {
  id: string;
  label: string;
}

export interface ReceiptAccuracyReport {
  weeks: ReceiptAccuracyWeeks;
  /** The first week's Monday and the last week's Sunday, fleet calendar. */
  start: string;
  end: string;
  parserId: string | null;
  vendorId: string | null;
  /** The reading mode now, so the page can say reading is off. */
  readingMode: ReceiptReadingMode;
  readingModeBasis: ReceiptReadingModeBasis;
  volume: ReceiptVolume;
  /** Top vendors, then `others` and `unknown` when they have receipts. */
  found: ReceiptFoundRow[];
  foundAll: ReceiptFoundRow;
  accuracy: ReceiptAccuracy;
  weekly: ReceiptTrendRow[];
  versions: ReceiptVersionRow[];
  vendors: ReceiptTrendRow[];
  confidence: ReceiptConfidenceRow[];
  /** The 25 newest corrections, newest first. */
  corrections: ReceiptCorrection[];
  options: { versions: ReceiptAccuracyOption[]; vendors: ReceiptAccuracyOption[] };
}

/** Vendors named on the page; the rest are "All others". */
export const RECEIPT_ACCURACY_TOP_VENDORS = 15;
/** Corrections listed. */
export const RECEIPT_ACCURACY_CORRECTIONS = 25;

export const RECEIPT_VENDOR_UNKNOWN_KEY = "unknown";
export const RECEIPT_VENDOR_OTHERS_KEY = "others";
